import React, { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Polyline, Marker } from '@react-google-maps/api';
import polyline from 'polyline-encoded';

const containerStyle = {
  width: '100%',
  height: '600px',
};

const defaultCenter = {
  lat: 12.9716,
  lng: 77.5946,
};

const COLORS = [
  '#FF1744', '#00E676', '#2979FF', '#FF9100', '#E91E63',
  '#00BCD4', '#FFC107', '#9C27B0', '#4CAF50', '#FF5722',
];

const RouteMap = ({ routeData, destinationLocation }) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState(null);
  const [selectedTripId, setSelectedTripId] = useState(null);

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  // Fit bounds when selected trip changes
  React.useEffect(() => {
    if (map && routeData && routeData.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      if (selectedTripId !== null) {
        // Fit to selected trip only
        const trip = routeData.find(t => t.tripId === selectedTripId);
        if (trip) {
          trip.suggestedRoutes.forEach((route) => {
            const decodedPath = polyline.decode(route.routePolyline);
            decodedPath.forEach(([lat, lng]) => {
              bounds.extend({ lat, lng });
            });
          });
        }
      } else {
        // Fit to all routes
        routeData.forEach((trip) => {
          trip.suggestedRoutes.forEach((route) => {
            const decodedPath = polyline.decode(route.routePolyline);
            decodedPath.forEach(([lat, lng]) => {
              bounds.extend({ lat, lng });
            });
          });
        });
      }

      if (destinationLocation) {
        bounds.extend(destinationLocation);
      }

      map.fitBounds(bounds);
    }
  }, [map, routeData, destinationLocation, selectedTripId]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (!isLoaded) {
    return <div className="loading">Loading Map...</div>;
  }

  return (
    <div className="map-container">
      <div className="trip-selector">
        <h3>Select Trip to Display:</h3>
        <div className="trip-buttons">
          <button
            className={selectedTripId === null ? 'active' : ''}
            onClick={() => setSelectedTripId(null)}
          >
            All Trips
          </button>
          {routeData.map((trip, index) => (
            <button
              key={trip.tripId}
              className={selectedTripId === trip.tripId ? 'active' : ''}
              onClick={() => setSelectedTripId(trip.tripId)}
              style={{ borderLeft: `4px solid ${COLORS[index % COLORS.length]}` }}
            >
              {trip.tripId} ({trip.employeeIds.length} employees)
            </button>
          ))}
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={12}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          streetViewControl: false,
          mapTypeControl: true,
        }}
      >
        {/* Destination Marker */}
        {destinationLocation && (
          <Marker
            position={destinationLocation}
            icon={{
              url: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
              scaledSize: new window.google.maps.Size(40, 40),
            }}
            label={{
              text: 'D',
              color: 'white',
              fontWeight: 'bold',
            }}
          />
        )}

        {/* Render Routes */}
        {routeData.map((trip, tripIndex) => {
          // Determine visibility
          const isVisible = selectedTripId === null || selectedTripId === trip.tripId;
          const isSelected = selectedTripId === trip.tripId;
          const color = COLORS[tripIndex % COLORS.length];

          return (
            <React.Fragment key={`trip-${trip.tripId}`}>
              {/* Render all polylines for this trip */}
              {trip.suggestedRoutes.map((route, routeIndex) => {
                const decodedPath = polyline.decode(route.routePolyline).map(([lat, lng]) => ({
                  lat,
                  lng,
                }));

                return (
                  <Polyline
                    key={`polyline-${trip.tripId}-${routeIndex}`}
                    path={decodedPath}
                    visible={isVisible}
                    options={{
                      strokeColor: color,
                      strokeOpacity: isSelected ? 1 : 0.6,
                      strokeWeight: isSelected ? 8 : 4,
                      zIndex: isSelected ? 1000 : tripIndex,
                    }}
                  />
                );
              })}

              {/* Render markers for each employee in this trip */}
              {trip.employeeIds.map((employeeId, empIndex) => {
                // Find the employee location from the route data
                let employeeLocation = null;
                
                // Check each route in this trip for this employee
                for (const route of trip.suggestedRoutes) {
                  const empIdIndex = route.employeeIds.indexOf(employeeId);
                  if (empIdIndex !== -1) {
                    const decodedPath = polyline.decode(route.routePolyline);
                    // Use the position in the path corresponding to this employee
                    // For simplicity, we'll use evenly distributed points along the route
                    const pointIndex = Math.min(
                      Math.floor((empIdIndex / route.employeeIds.length) * decodedPath.length),
                      decodedPath.length - 1
                    );
                    employeeLocation = {
                      lat: decodedPath[pointIndex][0],
                      lng: decodedPath[pointIndex][1],
                    };
                    break;
                  }
                }

                if (!employeeLocation) return null;

                return (
                  <Marker
                    key={`marker-${trip.tripId}-${employeeId}`}
                    position={employeeLocation}
                    visible={isVisible}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: isSelected ? 10 : 6,
                      fillColor: color,
                      fillOpacity: 1,
                      strokeColor: 'white',
                      strokeWeight: isSelected ? 3 : 2,
                    }}
                    label={{
                      text: String(empIndex + 1),
                      color: 'white',
                      fontSize: isSelected ? '12px' : '10px',
                      fontWeight: 'bold',
                    }}
                    title={`Employee ${employeeId}`}
                    zIndex={isSelected ? 1001 : 100 + tripIndex}
                  />
                );
              })}
            </React.Fragment>
          );
        })}
      </GoogleMap>

      {/* Legend */}
      <div className="route-legend">
        <h4>Route Information</h4>
        {routeData.map((trip, tripIndex) => {
          // Skip if a trip is selected and this is not that trip
          if (selectedTripId !== null && selectedTripId !== trip.tripId) {
            return null;
          }

          const color = COLORS[tripIndex % COLORS.length];
          const totalDistance = trip.suggestedRoutes.reduce(
            (acc, r) => acc + r.routeDistance,
            0
          );
          const totalDuration = trip.suggestedRoutes.reduce(
            (acc, r) => acc + r.routeDuration,
            0
          );

          return (
            <div key={`legend-${trip.tripId}`} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: color }}></div>
              <div className="legend-info">
                <strong>{trip.tripId}</strong>
                <div>Employees: {trip.employeeIds.join(', ')}</div>
                <div>Distance: {(totalDistance / 1000).toFixed(2)} km</div>
                <div>Duration: {Math.round(totalDuration / 60)} min</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RouteMap;
